// DEPLOY: /var/www/woody.wtf/frontend/src/components/OpenOrders.js
// Open orders table for the Trading pages (Portfolio tab, Trade page), with inline Modify
// (quantity, limit price, stop / trail amount - whichever the order type uses) and Cancel.
// Calls POST /api/trading/<broker>/order/<id>/modify and /cancel (trading.py).
import React, { useState } from 'react';
import api from '../api';
import { s, OPEN_STATUSES, qty, price, statusPill, errText, nameOf } from './tradingShared';

// Which price fields each IBKR order type can change: limit (price) and/or stop / trail (aux_price).
const EDITABLE = { LMT: ['price'], STP: ['aux_price'], 'STP LMT': ['price', 'aux_price'], TRAIL: ['aux_price'], MKT: [] };
const FIELD_LABEL = { price: 'Limit', aux_price: 'Stop' };

export default function OpenOrders({ broker, acc, orders, onChange, empty = 'No open orders.' }) {
  const [edit, setEdit] = useState(null);      // {order_id, qty, price, aux_price}
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState('');
  const [err, setErr]   = useState('');

  const cancel = async id => {
    if (!window.confirm(`Cancel order ${id}?`)) return;
    setMsg(''); setErr('');
    try { setMsg((await api.post(`/trading/${broker}/order/${id}/cancel`, { acc_id: acc.acc_id })).data.message); onChange(); }
    catch (e) { setErr(errText(e)); }
  };
  const save = async () => {
    setBusy(true); setMsg(''); setErr('');
    try {
      setMsg((await api.post(`/trading/${broker}/order/${edit.order_id}/modify`, { acc_id: acc.acc_id, ...edit })).data.message);
      setEdit(null); onChange();
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };

  if (!orders?.length) return <div style={{ ...s.meta, padding: '16px 0' }}>{empty}</div>;
  const cell = { ...s.td };
  const small = { ...s.input, width: '90px', padding: '3px 6px', fontSize: '12px' };

  return (
    <div style={{ overflowX: 'auto' }}>
      {msg && <div style={s.ok}>{msg}</div>}
      {err && <div style={s.err}>✕ {err}</div>}
      <table style={s.table}>
        <thead><tr>
          {['Code', 'Side', 'Qty', 'Type', 'Limit', 'Stop / trail', 'Filled', 'Status', 'TIF', 'Ext', 'Message', 'ID', ''].map(h =>
            <th key={h} style={{ ...s.th, textAlign: ['Qty', 'Limit', 'Stop / trail', 'Filled'].includes(h) ? 'right' : 'left' }}>{h}</th>)}
        </tr></thead>
        <tbody>{orders.map(r => {
          const editing = edit?.order_id === r.order_id;
          const fields = EDITABLE[r.order_type] || [];
          const open = OPEN_STATUSES.includes(r.order_status);
          const input = f => <input style={small} type="number" step="any" value={edit[f]} onChange={e => setEdit({ ...edit, [f]: e.target.value })} title={FIELD_LABEL[f]} />;
          return (
            <tr key={r.order_id}>
              <td style={cell}>{nameOf(r)}{r.parent_id && <span style={{ ...s.meta, marginLeft: '6px' }}>↳ {r.parent_id}</span>}</td>
              <td style={{ ...cell, color: r.side === 'BUY' ? 'var(--success)' : 'var(--error)' }}>{r.side}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{editing ? input('qty') : qty(r.qty)}</td>
              <td style={cell}>{r.order_type}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{editing && fields.includes('price') ? input('price') : fields.includes('price') ? price(r.price) : '—'}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{editing && fields.includes('aux_price') ? input('aux_price')
                : r.trail_pct ? `${r.trail_pct}%` : fields.includes('aux_price') ? price(r.aux_price) : '—'}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{`${qty(r.filled)} / ${qty((Number(r.filled) || 0) + (Number(r.remaining) || 0))}`}</td>
              <td style={cell}><span style={statusPill(r.order_status)}>{r.order_status}</span></td>
              <td style={cell}>{r.tif}</td>
              <td style={cell}>{r.outside_rth ? 'Yes' : ''}</td>
              <td style={{ ...cell, fontFamily: "'DM Sans', sans-serif", color: 'var(--text-muted)', whiteSpace: 'normal', minWidth: '160px' }}>{r.last_message || ''}</td>
              <td style={cell}>{r.order_id}</td>
              <td style={{ ...cell, textAlign: 'right' }}>
                {editing ? (
                  <>
                    <span style={{ ...s.link, marginRight: '10px' }} onClick={busy ? undefined : save}>{busy ? 'Saving…' : 'Save'}</span>
                    <span style={{ ...s.link, color: 'var(--text-muted)' }} onClick={() => setEdit(null)}>Cancel edit</span>
                  </>
                ) : open && (
                  <>
                    <span style={{ ...s.link, marginRight: '10px' }}
                          onClick={() => setEdit({ order_id: r.order_id, qty: String(r.qty ?? ''),
                                                   price: fields.includes('price') ? String(r.price ?? '') : '',
                                                   aux_price: fields.includes('aux_price') && !r.trail_pct ? String(r.aux_price ?? '') : '' })}>Modify</span>
                    <span style={{ ...s.link, color: 'var(--error)' }} onClick={() => cancel(r.order_id)}>Cancel</span>
                  </>
                )}
              </td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}
